import { format } from 'date-fns';

const fmtDate = (d: unknown): string => {
  if (!d || typeof d !== 'string') return '';
  try {
    const iso = d.length > 10 ? d.split('T')[0] : d;
    return format(new Date(iso + 'T00:00:00'), 'dd/MM/yyyy');
  } catch {
    return String(d);
  }
};

const joinList = (v: unknown, extra?: string): string => {
  const arr = Array.isArray(v) ? v : v ? [String(v)] : [];
  const items = [...arr.map((x) => (x == null ? '' : String(x)))];
  if (extra) items.push(extra);
  return items.filter((s) => s && s.trim() !== '').join(', ');
};

const buildSection = (
  title: string,
  rows: Array<[string, string | number | null | undefined]>,
): string => {
  const lines = rows
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .map(([k, v]) => `• ${k}: ${v}`);
  if (lines.length === 0) return '';
  return [`${title}`, '─────────────', ...lines].join('\n');
};

const buildBlock = (
  title: string,
  rows: Array<[string, string | number | null | undefined]>,
): string => {
  const lines = rows
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .map(([k, v]) => {
      const val = String(v);
      // multi-line values: put on a new indented line
      if (val.includes('\n') || val.length > 60) {
        const indented = val.split('\n').map((s) => `    ${s}`).join('\n');
        return `• ${k}:\n${indented}`;
      }
      return `• ${k}: ${val}`;
    });
  if (lines.length === 0) return '';
  return [`${title}`, '─────────────', ...lines].join('\n');
};

/**
 * Build the "copy to clipboard" text for a class trial.
 * Excludes: parent/student phone, parent contact info, sales (section 3) and teacher (section 4) fields.
 */
export function buildTrialCopyText(t: any): string {
  const fullName = [t?.student_firstname, t?.student_lastname].filter(Boolean).join(' ') || t?.student_name || '';
  const subject = t?.subject === 'อื่นๆ' ? (t?.other_subject || t?.otherSubject || 'อื่นๆ') : t?.subject;

  // Header line (น้อง + ชื่อเล่น + ระดับชั้น)
  const headerBits = [
    t?.student_nickname ? `น้อง${t.student_nickname}` : fullName ? `น้อง${fullName}` : '',
    t?.grade,
    subject,
  ].filter(Boolean).join(' • ');

  const sections = [
    headerBits ? `📋 ข้อมูลทดลองเรียน — ${headerBits}` : `📋 ข้อมูลทดลองเรียน`,
    buildBlock('👦 ข้อมูลนักเรียน', [
      ['ชื่อ-นามสกุล', fullName],
      ['ชื่อเล่น', t?.student_nickname],
      ['ระดับชั้น', t?.grade],
      ['โรงเรียน', t?.school],
      ['วิชา', subject],
      ['ต้องการเรียนเฉพาะทาง', joinList(t?.specialized_tutoring)],
      ['ภาษา', t?.language],
      ['ประเภทห้อง', t?.classroom_type],
      ['รูปแบบการเรียน', t?.learning_format],
      ['ประเภทคลาส', t?.class_type],
      ['สถานที่', t?.location],
      ['จำนวนชั่วโมง', t?.hours],
    ]),
    buildBlock('📅 วันเวลาทดลองเรียน', [
      ['วันที่เรียน', fmtDate(t?.class_date)],
      ['วันที่ต้องการทดลอง', fmtDate(t?.trial_day) || t?.trial_day],
      ['เวลาที่ต้องการทดลอง', t?.trial_time],
      ['เวลาเพิ่มเติม', t?.additional_trial_time],
      ['วันจริงที่ทดลอง', fmtDate(t?.actual_trial_day) || t?.actual_trial_day],
      ['เวลาจริงที่ทดลอง', t?.actual_trial_time],
      ['หัวข้อที่ต้องการทดลองเรียน', t?.trial_topic],
      ['วันที่ต้องการเริ่มเรียนจริง', fmtDate(t?.desired_start_date)],
      ['วันและเวลาที่ต้องการเรียนจริง', t?.convenient_time],
      ['ช่วงเวลาที่ไม่สะดวก', t?.inconvenient_time],
      ['เหตุผลที่ไม่สะดวก', t?.inconvenient_reason],
    ]),
    buildBlock('💡 ความคาดหวัง / ปัญหา', [
      ['สไตล์การสอนที่ต้องการ', joinList(t?.teaching_style)],
      ['ความกังวลของผู้ปกครอง', joinList(t?.parent_concerns, t?.parent_concerns_other)],
      ['ความคาดหวังจากการทดลอง', t?.parent_expectations_trial],
      ['ความคาดหวังจาก Fishsix', t?.parent_expectations_fishsix],
      ['เกณฑ์ความสำเร็จ', t?.success_criteria],
      ['หมายเหตุ', t?.note],
    ]),
  ].filter(Boolean).join('\n\n');

  return sections;
}